import { useEffect, useRef, useState } from "react"
import type { LoaderFunctionArgs, MetaFunction } from "react-router"
import {
  Link,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router"
import { ArrowUpRight } from "lucide-react"

import { pageMeta } from "~/lib/meta"
import { cn } from "~/lib/utils"
import { AUDIENCES, type Audience, type Job } from "~/data/audiences"
import { getPost } from "~/data/posts"
import { BannerPostCard, CompactPostRow } from "~/components/blog/PostCard"
import { Header } from "~/components/landing/Header"
import { Footer } from "~/components/landing/Footer"
import { AudienceTag } from "~/components/landing/AudienceArt"
import { NotFound, notFoundMeta } from "~/components/NotFound"

// /solutions/<audience> — one page per priority audience (from
// data/audiences.ts): the audience's dark hero card, the jobs it brings to
// Pine59 (each linking into the docs guide that carries the how-to depth) and
// "From the blog". There is no hub page: /solutions redirects to the first
// audience, the header's Solutions menu links here directly, and a pill row
// under the header moves between the pages.

export async function loader({ params }: LoaderFunctionArgs) {
  const audience = AUDIENCES.find((a) => a.id === params.audience)
  if (!audience) throw new Response("Not found", { status: 404 })
  return { id: audience.id }
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  // No data means the loader threw: the 404 below.
  const audience = data && AUDIENCES.find((a) => a.id === data.id)
  if (!audience) return notFoundMeta(location.pathname)
  return pageMeta({
    title: `${audience.name} · Solutions · Pine59`,
    description: audience.lede,
    pathname: location.pathname,
  })
}

// An unknown audience (/solutions/anything) is a 404 with a proper status.
// This route matches before the catch-all, so it needs its own boundary.
export function ErrorBoundary() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />
  return (
    <NotFound
      title="Something went wrong"
      body="This page failed to load. Try again in a moment, or head back to the front page."
    />
  )
}

// "From the blog" under an audience section — a long forest banner (built
// from Pine59's own topo-grid backdrop, see BannerPostCard) from sm up, rather
// than a grid, since most sections surface a single post here. Phones get the
// quiet one-line row instead: the banner is the loudest thing on a small
// screen. Placeholder posts still link (they show "coming soon").
function FromTheBlog({ slugs }: { slugs: string[] }) {
  const posts = slugs
    .map(getPost)
    .filter((p): p is NonNullable<typeof p> => p != null)
  if (posts.length === 0) return null

  return (
    <div className="mt-8 sm:mt-10">
      <div className="flex flex-col gap-6">
        {posts.map((p) => (
          <div key={p.slug}>
            <div className="hidden sm:block">
              <BannerPostCard post={p} />
            </div>
            <div className="sm:hidden">
              <CompactPostRow post={p} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Total grid cells a job list renders — one per job, plus one more for each
// job that also carries a bigImage (rendered as its own adjacent cell). The
// job grid's column count is picked off this, not the raw job count, so a
// job with a bigImage still pairs cleanly next to it instead of wrapping
// into the next row.
function cellCount(jobs: Job[]): number {
  return jobs.length + jobs.filter((j) => j.bigImage).length
}

// A job card's header: the icon and the title closed in one dark tag, as wide
// as the card. A rectangle with a small radius, not a pill: a pill reads as a
// button, and nothing in the header is clickable (the card has no link of its
// own). The icon is bare (no box or outline of its own) and sits beside the
// title on phones and desktop, and above it on tablet (sm to lg), where three
// cards share a narrow row and a side-by-side icon would squeeze the title.
// object-position (%) that centres a phone crop on the point `centre` (% of the
// art's width). object-position lines up the same percentage of the image and
// the frame, so it is not the centre itself: the visible window is
// `PHONE_VISIBLE` of the width, and its left edge must land at
// centre - PHONE_VISIBLE / 2. (Exact for 4:3; the 3:2 crop at sm is within
// about 2% of the width of it, which doesn't show.)
const PHONE_VISIBLE = 4 / 3 / (2000 / 933)
const cropPosition = (centre: number) => {
  const left = centre / 100 - PHONE_VISIBLE / 2
  return Math.round(Math.min(100, Math.max(0, (left / (1 - PHONE_VISIBLE)) * 100)))
}

function JobTag({ job }: { job: Job }) {
  const Icon = job.icon
  return (
    <h3 className="flex w-full flex-row items-center gap-3 rounded-md bg-forest px-4 py-3 font-sans text-[18px] font-semibold leading-tight tracking-tight text-paper sm:flex-col sm:items-start sm:gap-3 sm:p-4 lg:flex-row lg:items-center lg:px-4 lg:py-3 lg:text-[20px]">
      <Icon
        className="h-5 w-5 shrink-0 text-lime"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      {job.name}
    </h3>
  )
}

// The audience's page body. Spacing is deliberate: the gaps inside are
// 16-40px, so the hero, the jobs and the reading read as one group.
function AudienceSection({ audience }: { audience: Audience }) {
  // When 2+ jobs in this section share the same guide (several jobs are
  // different angles on the same underlying report — e.g. Brand Comparison
  // covers three separate Retail/Marketing jobs), link it once rather than
  // repeating an identical "See the guide" on every card that uses it.
  const shownGuides = new Set<string>()
  return (
    <section
      id={audience.id}
      aria-labelledby={`${audience.id}-title`}
      className="bg-paper"
    >
      <div className="u-container max-w-6xl py-14 md:py-20">
        {/* The hero: the same dark card as the landing page (the audience's own
            grainy forest composition from the Figma "dark" frames, on #082E16,
            the average colour of its grain), its name as a tag, and the "why"
            sentence in a rounded infobox right under it. From sm the image
            covers the whole card and the content sets a short height; on a
            phone the card is much taller than the wide, short art, so there it
            sits at the bottom at its natural size instead of being zoomed. */}
        <div className="relative overflow-hidden rounded-2xl bg-[#082E16]">
          {audience.landingArt ? (
            <img
              src={audience.landingArt}
              alt=""
              loading="lazy"
              decoding="async"
              // on a phone the top edge fades into the card, so the grain
              // never shows a seam against the flat card colour
              className="pointer-events-none absolute inset-x-0 bottom-0 h-auto w-full max-sm:[-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_8%)] max-sm:[mask-image:linear-gradient(to_bottom,transparent,#000_8%)] sm:inset-0 sm:h-full sm:object-cover sm:object-bottom"
            />
          ) : null}
          <div className="relative mx-auto flex max-w-4xl flex-col items-center gap-4 px-6 py-10 text-center sm:px-16">
            {/* The audience name is a label with its icon, the section's
                <h2>. The three action terms that used to sit under it are
                gone: they lived in the hero while the cards they pointed at
                sat far below, and readers couldn't connect the two. The
                cards carry that story themselves now. */}
            {audience.landingArt ? (
              <AudienceTag
                audience={audience}
                as="h1"
                size="lg"
                tone="dark"
                id={`${audience.id}-title`}
              />
            ) : (
              <h1 id={`${audience.id}-title`} className="sr-only">
                {audience.name}
              </h1>
            )}
            <p className="max-w-xl rounded-xl border border-paper/15 bg-paper/10 px-5 py-3.5 text-[14px] leading-relaxed text-paper/90 backdrop-blur-sm sm:px-6 sm:py-4 sm:text-[15px]">
              {audience.lede}
            </p>
          </div>
        </div>

        {/* the jobs. A plain audience (no real product screenshots yet)
            gets a grid of icon cards (icon, title, capability, link)
            instead of the old stake/guide two-column row — Anna's call:
            the stake/reality line that used to lead each card read as
            counterproductive, so capability is the whole card now, shown
            straight away rather than gated behind hover. An audience with
            real screenshots (bigImage) pairs each job with its own image
            as one flex row instead: a CSS Grid's column tracks don't
            shrink to the image's actual rendered width (object-contain
            keeps its own aspect ratio), which left a gap between the
            image and the card whenever the image didn't happen to fill
            its whole column — a flex row sizes the image to its own
            content width, so it always sits flush against the card. */}
        <div className="mt-8">
          {audience.jobs.some((j) => j.bigImage) ? (
            <div className="flex flex-col gap-6">
              {audience.jobs.map((job, index) => {
                const showGuide = job.guide != null && !shownGuides.has(job.guide)
                if (job.guide) shownGuides.add(job.guide)
                // Alternate which side the image sits on per row (Heidi
                // does the same with flex-row-reverse) — the card stays
                // first in DOM order either way, so reading order and
                // screen readers aren't affected, only the visual position.
                const imageFirst = index % 2 === 1
                return (
                  // Card and image wrapped in one surface (shared bg,
                  // rounded only on the outside) rather than two separate
                  // rounded boxes side by side — Anna's call: they should
                  // read as one unit, not two cards that happen to touch.
                  <div
                    key={job.name}
                    className={cn(
                      "flex flex-col overflow-hidden rounded-2xl",
                      // A wide image can't take its lg: width (up to 42rem)
                      // beside the text on a tablet, so from md: it splits
                      // the row 50/50 with the text card (see the wrapper
                      // below) and only grows to its full width from lg:.
                      // Text and image sit side by side from md up.
                      job.bigImageWide ? "md:flex-row" : "sm:flex-row",
                      imageFirst && (job.bigImageWide ? "md:flex-row-reverse" : "sm:flex-row-reverse"),
                      "bg-cream/60",
                    )}
                  >
                    <div className="group/card relative flex flex-1 flex-col gap-3 p-6">
                      <JobTag job={job} />
                      <p className="max-w-[42ch] font-flex text-[16px] leading-snug text-ink/70">
                        {job.capability}
                      </p>
                      {/* Every job points straight to its Docs guide —
                          Pine59 doesn't build separate internal capability
                          pages. A job with no real guide gets no link at
                          all, rather than one pointing at the generic
                          guides index. Space is reserved (not collapsed)
                          so hover only toggles opacity — a height-collapse
                          reveal would push everything below the card down
                          when it opens. */}
                      {showGuide ? (
                        <a
                          href={job.guide}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-sm text-[13px] font-medium text-chartreuse-deep opacity-0 transition-opacity duration-200 ease-in-out hover:text-forest focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-chartreuse-deep group-focus-within/card:opacity-100 group-hover/card:opacity-100 [@media(hover:none)]:opacity-100"
                        >
                          {job.guideLabel ?? "See the guide"}
                          <ArrowUpRight className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                        </a>
                      ) : null}
                    </div>

                    {job.bigImage ? (
                      // Sized to its own content (not a grid column), so it
                      // always sits flush against the card. object-contain
                      // shows the whole asset with no cropping. The
                      // wrapper's fill matches the text card's own bg
                      // (not bg-forest) so the two halves read as one
                      // continuous surface — only the image itself still
                      // carries its own dark backdrop, inset by the
                      // padding. The wrapper's own corners stay square (it
                      // touches the outer row's rounded edge via that
                      // row's overflow-hidden); the image itself is
                      // rounded since padding now insets it from that
                      // edge. w-full on mobile so it spans the full card
                      // width when stacked instead of the sm+ width cap
                      // applying there too.
                      <div
                        className={cn(
                          "flex w-full shrink-0 items-center justify-center p-4 sm:p-6",
                          "bg-cream/60",
                          job.bigImageWide
                            ? // tablet: the image takes 3/5 of the row (it was 1/2)
                              // with less padding around it, so it can scale up;
                              // from lg it is back to its full width cap
                              "md:w-3/5 md:p-3 lg:w-full lg:max-w-2xl lg:p-6"
                            : "sm:max-w-xs",
                        )}
                      >
                        <img
                          src={job.bigImage}
                          // Wide images ship at 2000px and 1000px: a phone shows
                          // them at ~295px, a desktop at ~620px, so only a
                          // retina desktop needs the 2000px file (the 1000px
                          // one is a quarter of its weight). `sizes` mirrors
                          // the layout below: the image wrapper's width minus
                          // padding. width/height give it its ratio before it
                          // loads (the md+ layout has no aspect-ratio class).
                          srcSet={
                            job.bigImageWide
                              ? `${job.bigImage.replace(/\.webp$/, "-1000.webp")} 1000w, ${job.bigImage} 2000w`
                              : undefined
                          }
                          sizes={
                            job.bigImageWide
                              ? "(min-width: 1024px) 624px, (min-width: 768px) 55vw, calc(100vw - 80px)"
                              : undefined
                          }
                          width={job.bigImageWide ? 2000 : undefined}
                          height={job.bigImageWide ? 933 : undefined}
                          alt={`${job.name} in Pine59`}
                          loading="lazy"
                          decoding="async"
                          className={cn(
                            "rounded-2xl",
                            job.bigImageWide
                              ? // Stacked (below md) the wide screenshot is cropped
                                // to the card's width instead of shrunk into it. The
                                // interface is 40-60% of the art's width, so a 4:3
                                // (phones, shows 62% of the width) or 3:2 (sm, 70%)
                                // crop keeps all of it, ~1.6x larger, as long as the
                                // crop is centred on it: see bigImageFocus below.
                                // From md it is beside the text again, at its own
                                // ratio (object-contain, so the focus is moot).
                                "aspect-[4/3] w-full object-cover sm:aspect-[3/2] md:aspect-auto md:h-full md:w-auto md:max-w-full md:object-contain"
                              : "h-52 w-auto max-w-full object-contain sm:h-full",
                          )}
                          style={
                            job.bigImageWide
                              ? { objectPosition: `${cropPosition(job.bigImageFocus ?? 50)}% 50%` }
                              : undefined
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              className={cn(
                "grid gap-4",
                cellCount(audience.jobs) % 2 === 0 ? "sm:grid-cols-2" : "sm:grid-cols-3",
              )}
            >
              {audience.jobs.map((job) => {
                const showGuide = job.guide != null && !shownGuides.has(job.guide)
                if (job.guide) shownGuides.add(job.guide)
                return (
                  <div
                    key={job.name}
                    className={cn(
                      // Each card spans three rows of the grid (tag, text, link) and shares
                      // them with its neighbours (subgrid), so the tags in a row are
                      // always the same height and the text lines up, even when one
                      // title wraps on a narrow tablet column.
                      "group/card relative grid row-span-3 grid-rows-subgrid gap-y-3 rounded-2xl p-6",
                      "bg-cream/60",
                    )}
                  >
                    <JobTag job={job} />
                    <p className="font-flex text-[16px] leading-snug text-ink/70">
                      {job.capability}
                    </p>
                    {showGuide ? (
                      <a
                        href={job.guide}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-sm text-[13px] font-medium text-chartreuse-deep opacity-0 transition-opacity duration-200 ease-in-out hover:text-forest focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-chartreuse-deep group-focus-within/card:opacity-100 group-hover/card:opacity-100 [@media(hover:none)]:opacity-100"
                      >
                        {job.guideLabel ?? "See the guide"}
                        <ArrowUpRight className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                      </a>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* further reading */}
        {audience.readingSlugs && audience.readingSlugs.length > 0 ? (
          <FromTheBlog slugs={audience.readingSlugs} />
        ) : null}

      </div>
    </section>
  )
}

// The pill row under the site header: every audience, the current page's
// pill filled. Same forest bar as the old one-page scroll-spy nav, but each
// pill is a route link now.
//
// On phones the six pills don't fit, so the row scrolls sideways: no
// scrollbar, each swipe snaps to a pill, it opens with the current pill
// centred, and a soft forest fade sits on whichever edge still has pills
// beyond it (and disappears at that end), so the cut-off pill reads as
// "more this way" rather than a clipped layout.
function AudiencePills({ activeId }: { activeId: string }) {
  const pillsRef = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState({ left: false, right: false })

  useEffect(() => {
    const row = pillsRef.current
    if (!row) return
    const update = () => {
      // 4px of slack so sub-pixel scroll positions don't leave a fade on
      const left = row.scrollLeft > 4
      const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 4
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }))
    }
    update()
    row.addEventListener("scroll", update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(row)
    return () => {
      row.removeEventListener("scroll", update)
      ro.disconnect()
    }
  }, [])

  useEffect(() => {
    const row = pillsRef.current
    const pill = row?.querySelector<HTMLElement>(`[data-audience="${activeId}"]`)
    if (!row || !pill) return
    // Scroll the row itself, not scrollIntoView(): that can also scroll the
    // page.
    row.scrollTo({
      left: pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2,
    })
  }, [activeId])

  return (
    <nav
      aria-label="Solutions by audience"
      className="sticky top-16 z-40 border-b border-line bg-forest lg:top-[91px]"
    >
      <div
        ref={pillsRef}
        className="u-container relative flex h-12 max-w-5xl snap-x snap-mandatory items-center gap-1.5 overflow-x-auto [scrollbar-width:none] sm:h-14 sm:snap-none sm:gap-2 [&::-webkit-scrollbar]:hidden"
      >
        {AUDIENCES.map((a) => (
          <Link
            key={a.id}
            to={`/solutions/${a.id}`}
            data-audience={a.id}
            aria-current={a.id === activeId ? "page" : undefined}
            className={cn(
              "inline-flex shrink-0 snap-center items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-medium transition-colors sm:px-4 sm:py-1.5 sm:text-[14px]",
              a.id === activeId
                ? "border-lime bg-lime text-forest sm:bg-lime/15 sm:text-lime"
                : "border-paper/20 text-paper/60 hover:border-lime hover:text-lime",
            )}
          >
            {a.name}
          </Link>
        ))}
      </div>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-forest to-transparent transition-opacity duration-200",
          more.left ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-forest to-transparent transition-opacity duration-200",
          more.right ? "opacity-100" : "opacity-0",
        )}
      />
    </nav>
  )
}

export default function SolutionsAudience() {
  const { id } = useLoaderData<typeof loader>()
  const audience = AUDIENCES.find((a) => a.id === id)!
  const { hash } = useLocation()
  const navigate = useNavigate()

  // Old one-page links (/solutions#civic) arrive here via the /solutions
  // redirect with the fragment intact: forward them to that audience's page.
  useEffect(() => {
    const target = hash.slice(1)
    if (target !== id && AUDIENCES.some((a) => a.id === target))
      navigate(`/solutions/${target}`, { replace: true })
  }, [hash, id, navigate])

  return (
    <div className="bg-paper">
      <Header />

      {/* The pills and <main> share one wrapper so the sticky row's scope
          ends with the page body instead of riding over the footer. */}
      <div>
        <AudiencePills activeId={audience.id} />
        <main>
          <AudienceSection key={audience.id} audience={audience} />
        </main>
      </div>

      <Footer />
    </div>
  )
}
